import React, { ComponentType, useEffect, useState } from 'react'
import { useCtx } from './Ctx'
import hoistNonReactStatics from 'hoist-non-react-statics'

type HtmlTagPropsType = {
  className?: string
}

export const useHtmlTag = process.server
  ? // server 时，在 constructor 上，写入 internalScope，_document 读取写入页面标签
    (props: HtmlTagPropsType) => {
      const ctx = useCtx()
      // constructor
      if (process.server) {
        ctx.__AL_EX_internalScope.extraDocProps.className = props.className
      }
    }
  : // client 时，在 willMount、willUnmount、props 变化时，用原生 JS 修改 html 标签
    (props: HtmlTagPropsType) => {
      const [prevProps, setPrevProps] = useState<HtmlTagPropsType>()
      if (prevProps !== props) {
        setPrevProps(props)
      }

      // 考虑可能带来绘制抖动(class切换)， 额外在 constructor 上多执行一次
      changeClassNameByBrowserApi(props.className)

      useEffect(() => {
        // componentDidMount
        changeClassNameByBrowserApi(props.className)
        return function cleanup() {
          // componentWillUnmount
          changeClassNameByBrowserApi(undefined, prevProps!.className)
        }
      }, [prevProps, props.className])

      // componentWillReceiveProps | getDerivedStateFromProps
      if (prevProps && props.className !== prevProps!.className) {
        changeClassNameByBrowserApi(props.className, prevProps!.className)
      }
    }

function changeClassNameByBrowserApi(
  newClassName?: string,
  prevClassName?: string
) {
  if (prevClassName) {
    document.documentElement.classList.remove(prevClassName)
  }
  if (newClassName) {
    document.documentElement.classList.add(newClassName)
  }
}

/**
 * 在组件的生命周期上，额外的操作 <html> 标签
 *
 * @param tagProps
 *
 * @example
 *
 *  // 装饰器
 *  @withHtmlTag({className: 'xxx}) class extends React.Component {}
 *
 *  // HOC
 *  withHtmlTag({className: 'xxx'})<UserProps>(props => <Component/>)
 *
 *  // hook
 *  (props) => { useHtmlTag({className: 'xxx'}); return <Component/>; }
 *
 */
export function withHtmlTag(tagProps: HtmlTagPropsType) {
  return function wrap<T>(Component: ComponentType<T>) {
    const WithHtmlTag: React.FunctionComponent<T> = React.memo((props) => {
      useHtmlTag(tagProps)
      return <Component {...props} />
    })
    WithHtmlTag.displayName = `alExWithHtmlTag(${
      Component.displayName || Component.name || 'Component'
    })`
    hoistNonReactStatics(WithHtmlTag, Component)
    return WithHtmlTag
  }
}
